import React, { useMemo } from 'react';
import sirrisLogo from '@/assets/logos/sirris.svg';
import agoriaLogo from '@/assets/logos/agoria.svg';
import edenredLogo from '@/assets/logos/edenred.png';
import samsicLogo from '@/assets/logos/samsic.png';
import billieBonkersLogo from '@/assets/logos/billie-bonkers.png';
import imecIstartLogo from '@/assets/logos/imec-istart.png';
import arrowLogo from '@/assets/logos/arrow.png';
import arvalLogo from '@/assets/logos/arval.png';
import mieleLogo from '@/assets/logos/miele.png';
import partenaLogo from '@/assets/logos/partena.png';
import cipalLogo from '@/assets/logos/cipal-schaubroeck.png';
import penneoLogo from '@/assets/logos/penneo.png';
import exactLogo from '@/assets/logos/exact.jpg';
import bmwLogo from '@/assets/logos/bmw.webp';
import greifLogo from '@/assets/logos/greif.png';
import signifyLogo from '@/assets/logos/signify.jpg';
import proximusLogo from '@/assets/logos/proximus.png';
import lenovoLogo from '@/assets/logos/lenovo.png';
import creativeShelterLogo from '@/assets/logos/creative-shelter.png';
import imecLogo from '@/assets/logos/imec.png';
import modularLightingLogo from '@/assets/logos/modular-lighting.jpg';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

const ClientLogoStrip = () => {
  const autoplayPlugin = useMemo(
    () => Autoplay({ delay: 2500, stopOnInteraction: false, stopOnMouseEnter: true }),
    []
  );

  const clients: { name: string; logo: string; scale: 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge' }[] = [
    { name: 'BMW', logo: bmwLogo, scale: 'xxlarge' },
    { name: 'Proximus', logo: proximusLogo, scale: 'xxlarge' },
    { name: 'Lenovo', logo: lenovoLogo, scale: 'xlarge' },
    { name: 'Imec', logo: imecLogo, scale: 'xxlarge' },
    { name: 'Signify', logo: signifyLogo, scale: 'xxlarge' },
    { name: 'Greif', logo: greifLogo, scale: 'large' },
    { name: 'Exact', logo: exactLogo, scale: 'xxlarge' },
    { name: 'Modular Lighting', logo: modularLightingLogo, scale: 'xxlarge' },
    { name: 'Creative Shelter', logo: creativeShelterLogo, scale: 'xxxlarge' },
    { name: 'Cipal Schaubroeck', logo: cipalLogo, scale: 'medium' },
    { name: 'Miele Professional', logo: mieleLogo, scale: 'large' },
    { name: 'Partena Professional', logo: partenaLogo, scale: 'large' },
    { name: 'Penneo', logo: penneoLogo, scale: 'large' },
    { name: 'Edenred', logo: edenredLogo, scale: 'large' },
    { name: 'Sirris', logo: sirrisLogo, scale: 'large' },
    { name: 'Agoria', logo: agoriaLogo, scale: 'medium' },
    { name: 'Arval', logo: arvalLogo, scale: 'large' },
    { name: 'Samsic', logo: samsicLogo, scale: 'xlarge' },
    { name: 'Imec.istart', logo: imecIstartLogo, scale: 'xxxlarge' },
    { name: 'Billie Bonkers', logo: billieBonkersLogo, scale: 'xxxlarge' },
    { name: 'Arrow', logo: arrowLogo, scale: 'xxxlarge' },
  ];

  const scaleClasses = {
    medium: 'h-8 md:h-10 max-w-[100px] md:max-w-[120px]',
    large: 'h-10 md:h-12 max-w-[120px] md:max-w-[140px]',
    xlarge: 'h-12 md:h-14 max-w-[140px] md:max-w-[160px]',
    xxlarge: 'h-20 md:h-24 max-w-[240px] md:max-w-[280px]',
    xxxlarge: 'h-32 md:h-40 max-w-[600px] md:max-w-[700px]',
  };

  const scaleDimensions: Record<string, { width: number; height: number }> = {
    medium: { width: 100, height: 40 },
    large: { width: 120, height: 48 },
    xlarge: { width: 140, height: 56 },
    xxlarge: { width: 240, height: 96 },
    xxxlarge: { width: 600, height: 240 },
  };

  return (
    <section className="py-12 bg-background border-y border-border/50">
      <div className="container">
        <p className="text-center text-sm text-gray-600 mb-8 uppercase tracking-wider font-medium">
          Vertrouwd door marktleiders in België
        </p>
        <div className="relative">
          {/* Fade gradients */}
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          <Carousel
            opts={{
              align: 'start',
              loop: true,
            }}
            plugins={[autoplayPlugin as any]}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {clients.map((client) => (
                <CarouselItem
                  key={client.name}
                  className="pl-4 basis-1/2 sm:basis-1/2 md:basis-1/3 lg:basis-1/5"
                >
                  <div
                    className="flex items-center justify-center h-32 md:h-40 opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
                    title={client.name}
                  >
                    <img
                      src={client.logo}
                      alt={client.name}
                      className={`${scaleClasses[client.scale]} w-auto object-contain`}
                      loading="lazy"
                      decoding="async"
                      width={scaleDimensions[client.scale].width}
                      height={scaleDimensions[client.scale].height}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default ClientLogoStrip;
